import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import type { AuthClient } from 'features/auth/AuthClient';
import { authStateChanged, restoreSession } from 'features/auth/authSlice';

import { apiMiddleware, type RootState, rootReducer } from './reducers';

type CreateAppStoreOptions = {
    authClient: AuthClient;
    preloadedState?: Partial<RootState>;
    initializeAuth?: boolean;
};

export const createAppStore = ({ authClient, preloadedState, initializeAuth = true }: CreateAppStoreOptions) => {
    const store = configureStore({
        reducer: rootReducer,
        middleware: getDefaultMiddleware =>
            getDefaultMiddleware({ thunk: { extraArgument: authClient } }).concat(apiMiddleware),
        preloadedState,
    });

    const unsubscribeFromAuth = initializeAuth
        ? authClient.subscribe?.(user => store.dispatch(authStateChanged(user)))
        : undefined;

    if (initializeAuth) {
        void store.dispatch(restoreSession());
    }

    /*
     * Wires the window focus and online events into RTK Query. Without this, `refetchOnFocus` and
     * `refetchOnReconnect` are inert even where an endpoint or hook opts in. It attaches listeners
     * to `window`, so the teardown is returned through `dispose`.
     */
    const unsubscribeFromApiListeners = setupListeners(store.dispatch);

    return Object.assign(store, {
        dispose: () => {
            unsubscribeFromAuth?.();
            unsubscribeFromApiListeners();
        },
    });
};

export type AppStore = ReturnType<typeof createAppStore>;
export type AppDispatch = AppStore['dispatch'];
